import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import Empty from "components/Empty";
import LoadingSpinner from "components/LoadingSpinner";
import useCustomers from "hooks/api/customers/useCustomers";
import useQueryParam, { QUERY_STRING_KEYS } from "hooks/useQueryParam";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { useAuth } from "./AuthProvider";

export type SelectedCustomerState = {
  selectedCustomerCodes: string[];
  selectedCustomers: Customer[];
};

const CustomerContext = createContext<SelectedCustomerState>(
  {} as SelectedCustomerState
);

const CustomerProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const { userId } = useAuth();
  const { data: customers, isLoading: customersIsLoading } = useCustomers();

  const [, setSelectedCustomerCodes] = useQueryParam(
    QUERY_STRING_KEYS.SELECTED_CUSTOMER_KEY
  );
  const selectedCustomerCodes = useSelectedCustomerCode();

  const [selectedCustomers, setSelectedCustomers] = useState<Array<Customer>>(
    []
  );

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const SAVED_SELECTED_CUSTOMER_CODES = `SAVED_SELECTED_CUSTOMER_CODES${userId}`;

    const setAndSaveSelectedCustomers = (customers: Array<Customer>) => {
      setSelectedCustomerCodes(customers.map((c) => c.code).join(","));
      setSelectedCustomers(customers);
      window.localStorage.setItem(
        SAVED_SELECTED_CUSTOMER_CODES,
        customers.map((c) => c.code).join(",")
      );
    };

    if (!customersIsLoading) {
      const savedSelectedCustomerCodes =
        window.localStorage
          .getItem(SAVED_SELECTED_CUSTOMER_CODES)
          ?.split(",") ?? [];

      if (customers && customers.length > 0) {
        // if query parameter has no customer codes check local storage
        const defaultCustomerCodes =
          selectedCustomerCodes.length === 0
            ? savedSelectedCustomerCodes
            : selectedCustomerCodes;

        const defaultCustomers = customers.filter((_customer) =>
          defaultCustomerCodes.includes(_customer.code)
        );

        if (defaultCustomers.length === 0) {
          setAndSaveSelectedCustomers([customers?.[0]]);
        } else if (
          selectedCustomers.map((c) => c.code).join(",") !==
          defaultCustomers.map((c) => c.code).join(",")
        ) {
          setAndSaveSelectedCustomers(defaultCustomers);
        }
      }
      setIsLoading(false);
    }
  }, [
    customers,
    selectedCustomers,
    selectedCustomerCodes,
    setSelectedCustomerCodes,
    customersIsLoading,
    userId,
  ]);

  const value = useMemo<SelectedCustomerState>(
    () => ({
      selectedCustomers,
      selectedCustomerCodes,
    }),
    [selectedCustomers, selectedCustomerCodes]
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!customers || customers.length === 0) {
    return (
      <Empty
        title="No contracts yet"
        maxWidth="400px"
        message="Once approved, contracts will appear here."
      />
    );
  }

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomerContext = (): SelectedCustomerState => {
  const customerContext = useContext(CustomerContext);
  if (!customerContext) {
    throw new Error("Customer context not initialized");
  }

  return customerContext;
};

export default CustomerProvider;
